import { Component, computed, effect, input, model, output, viewChild } from '@angular/core';
import { UiModule } from '../../../components/ui.module';
import { Branch, GetBranchesRequest, LocationResponse, LocationTiny } from '../../../types';
import { LocationGroup } from '../../../types/location-group-response';
import { PageSelectBranchComponent } from '../page-select-branch/page-select-branch.component';
import { PageSelectLocationGroupComponent } from '../page-select-location-group/page-select-location-group.component';
import { PageSelectLocationComponent } from '../page-select-location/page-select-location.component';

@Component({
  selector: 'app-page-filters',
  templateUrl: './page-filters.component.html',
  styleUrl: './page-filters.component.scss',
  standalone: true,
  imports: [PageSelectBranchComponent, PageSelectLocationComponent, PageSelectLocationGroupComponent, UiModule],
})
export class PageFiltersComponent {
  private oneDayMs = 1000 * 60 * 60 * 24;

  public dateRangeStartMin = computed(() => {
    const end = this.dateRangeEnd();
    const maxDays = this.dateRangeMaximumDays();
    if (end && maxDays !== undefined) {
      return new Date(end.getTime() - maxDays * 24 * 60 * 60 * 1000);
    }
    return undefined;
  });

  public dateRangeEndMax = computed(() => {
    const start = this.dateRangeStart();
    const maxDays = this.dateRangeMaximumDays();
    if (start && maxDays !== undefined) {
      return new Date(start.getTime() + maxDays * 24 * 60 * 60 * 1000);
    }
    return undefined;
  });

  public dateRangeMaximumDays = input<number | undefined>(undefined);

  constructor() {
    effect(() => {
      const start = this.dateRangeStart();
      const end = this.dateRangeEnd();
      const maxDays = this.dateRangeMaximumDays();
      if (start && end && maxDays !== undefined) {
        const diff = (end.getTime() - start.getTime()) / this.oneDayMs;
        if (diff > maxDays) {
          if (start > end) {
            const newStart = new Date(end.getTime() - maxDays * this.oneDayMs);
            this.dateRangeStart.set(newStart);
          } else {
            const newEnd = new Date(start.getTime() + maxDays * this.oneDayMs);
            this.dateRangeEnd.set(newEnd);
          }
        }
      }
    });
  }

  public selectedBranches = model<Branch[] | Branch | undefined>(undefined);
  public allowBranchSelection = input<boolean>(false);
  public branchSelectionLabel = input<string>('Select Branch');
  public branchSelectionMulti = input<boolean>(false);
  public branchSelectionDisabled = input<boolean>(false);
  public branchSelectionShowExternalId = input<boolean>(false);
  public branchSelectionSelectDefault = input<boolean>(false);
  public branchSelectionAutoSelectBranchIds = input<number[]>(undefined);
  public branchOverrideBranches = input<Branch[]>([]);
  public branchRequestParams = input<Partial<GetBranchesRequest>>({});
  public branchFilterFn = input<((location: Branch) => boolean) | undefined>(() => true);
  public branchDefaultSelected = output<Branch>();
  public branchSelector = viewChild(PageSelectBranchComponent);

  public selectedLocations = model<LocationTiny[] | LocationTiny | undefined>(undefined);
  public allowLocationSelection = input<boolean>(false);
  public locationSelectionLabel = input<string>('Select Location');
  public locationSelectionMulti = input<boolean>(false);
  public locationSelectionDisabled = input<boolean>(false);
  public locationSelectionSelectDefault = input<boolean>(false);
  public locationSelectionAutoSelectLocationIds = input<number[]>(undefined);
  public locationOverrideLocations = input<LocationTiny[]>([]);
  public locationForceShowOverrideLocationsEvenIfEmpty = input<boolean>(false);
  public locationShowBranchName = input<boolean>(true);
  public locationFilterFn = input<((location: LocationResponse) => boolean) | undefined>(() => true);
  public locationDefaultSelected = output<LocationTiny>();
  public locationSelector = viewChild(PageSelectLocationComponent);

  public selectedLocationGroups = model<LocationGroup[] | LocationGroup | undefined>(undefined);
  public allowLocationGroupSelection = input<boolean>(false);
  public locationGroupSelectionLabel = input<string>('Select Location Group');
  public locationGroupSelectionMulti = input<boolean>(false);
  public locationGroupSelectionDisabled = input<boolean>(false);
  public locationGroupSelectionShowExternalId = input<boolean>(false);
  public locationGroupSelectionSelectDefault = input<boolean>(false);
  public locationGroupSelectionAutoSelectLocationIds = input<string[]>(undefined);
  public locationGroupOverrideLocations = input<LocationGroup[]>([]);
  public locationGroupDefaultSelected = output<LocationGroup>();
  public locationGroupSelector = viewChild(PageSelectLocationGroupComponent);

  public allowDateRangeSelection = input<boolean>(false);
  public dateRangeShowClear = input<boolean>(false);
  public dateRangeStart = model<Date | undefined>(undefined);
  public dateRangeStartLabel = input<string>('Select Date Range Start');
  public dateRangeStartDisabled = input<boolean>(false);
  public dateRangeEnd = model<Date | undefined>(undefined);
  public dateRangeEndLabel = input<string>('Select Date Range End');
  public dateRangeEndDisabled = input<boolean>(false);
  public showIncidentIcon = input<boolean>(false);
  public doNaturalSort = input<boolean>(false);
}
